import { Button, Card, Typography } from "antd"
import Layout from "modules/layout/dashboard"
import { useInvoiceId, useMemberId } from "pages/member"
import { useMember } from "pages/member/queries"
import { useInvoice, usePaymentMethods } from "../../queries"
import { Link, useNavigate } from "react-router-dom"
import InteracLogo from "assets/interac-logo.webp"
import { ReactComponent as InvoiceIcon } from "assets/icons/invoice.svg"
import { ReactComponent as VisaIcon } from "assets/icons/visa.svg"
import { ReactComponent as MastercardIcon } from "assets/icons/master-card.svg"
import { ReactComponent as LockIcon } from "assets/icons/lock.svg"
import { ReactComponent as InfoIcon } from "assets/icons/info.svg"
import styles from "./styles.module.scss"
import { useTranslation } from "react-i18next"
import { useEffect } from "react"
import Loader from "components/loader"

const { Main } = Layout
const { Title, Text } = Typography

const Payment: React.FC = () => {
  const { t } = useTranslation()
  const memberId = useMemberId()
  const member = useMember(memberId)
  const invoiceId = useInvoiceId()
  const invoice = useInvoice(memberId!, invoiceId!)
  const paymentMethods = usePaymentMethods()
  const navigate = useNavigate()

  const paymentMethodsOptions = [
    {
      key: "interac",
      logo: (
        <img src={InteracLogo} alt="Interac" className={styles.methodLogo} />
      ),
      title: t("invoice.payment_interac_title"),
      description: t("invoice.payment_interac_desc"),
      buttonLabel: t("invoice.payment_interac_btn"),
      link: "interac_pay",
    },
    {
      key: "card",
      logo: (
        <div className={styles.cardLogos}>
          <VisaIcon width={50} height={50} />
          <MastercardIcon width={50} height={50} />
        </div>
      ),
      title: t("invoice.payment_card_title"),
      description: t("invoice.payment_card_desc"),
      buttonLabel: t("invoice.payment_card_btn"),
      link: "credit_card_pay",
    },
  ]

  useEffect(() => {
    invoice.data &&
      ["paid", "recoverable", "canceled"].includes(invoice.data?.status!) &&
      navigate("../")
  }, [invoice.data])

  if (paymentMethods.isLoading || member.isLoading || invoice.isLoading) {
    return <Loader />
  }

  return (
    <>
      <Main.PageHeader
        breadcrumbs={[
          {
            title: member.data?.name,
            link: `/members/${member.data?.id}/events`,
          },
          {
            title: t("invoice.title"),
            link: `/members/${member.data?.id}/invoices`,
          },
          {
            title: invoice.data?.reference ?? invoice.data?.proforma_reference,
            link: `/members/${member.data?.id}/invoices/${invoice.data?.id}`,
          },
          { title: t("invoice.payment") },
        ]}
        onButtonClick={() =>
          navigate(`/members/${memberId}/invoices/${invoiceId}`)
        }
        title={
          <Title level={3}>
            {t("invoice.pay_invoice_title")} No:{" "}
            {invoice.data?.reference ?? invoice.data?.proforma_reference}
          </Title>
        }
      />

      <Card className={styles.sectionCard}>
        <div className={styles.sectionHeader}>
          <Title level={5} className={styles.sectionTitle}>
            {t("invoice.online_payment_title")}
          </Title>
          <span className={styles.recommendedBadge}>
            {t("invoice.recommended")}
          </span>
        </div>
        <Text className={styles.sectionSubtitle}>
          {t("invoice.online_payment_desc")}
        </Text>

        <div className={styles.methodsList}>
          {paymentMethodsOptions.map((method) =>
            paymentMethods.data?.some(({ name }) => name === method.key) ? (
              <Link
                key={method.key}
                className={styles.methodRow}
                to={method.link}
              >
                <div className={styles.methodLogoWrapper}>{method.logo}</div>
                <div className={styles.methodInfo}>
                  <Text strong className={styles.methodTitle}>
                    {method.title}
                  </Text>
                  <Text className={styles.methodDesc}>
                    {method.description}
                  </Text>
                </div>
                <div className={styles.methodAction}>
                  <Button type="primary" className={styles.methodBtn}>
                    {method.buttonLabel}
                  </Button>
                  <span className={styles.chevron}>›</span>
                </div>
              </Link>
            ) : null
          )}
        </div>

        <div className={styles.payOtherwiseSection}>
          <Title level={5} className={styles.payOtherwiseTitle}>
            {t("invoice.pay_otherwise_title")}
          </Title>
          <Text className={styles.payOtherwiseDesc}>
            {t("invoice.pay_otherwise_desc")}
          </Text>
          <div className={styles.downloadRow}>
            <InvoiceIcon className={styles.downloadIcon} />
            <span>
              <a
                href={`${
                  import.meta.env.VITE_APP_API_URL
                }/operation/administration/members/${memberId}/invoices/${invoiceId}/export`}
                target="_blank"
                rel="noreferrer"
                className={styles.downloadLink}
              >
                {t("invoice.download_invoice")}
              </a>
              <Text>{t("invoice.download_invoice_suffix")}</Text>
            </span>
          </div>
        </div>

        <div className={styles.infoBanner}>
          <InfoIcon className={styles.infoIcon} width={20} height={20} />
          <Text>{t("invoice.payment_notification_info")}</Text>
        </div>
        <div className={styles.secureFooter}>
          <LockIcon className={styles.lockIcon} />
          <Text className={styles.secureText}>
            {t("invoice.secure_payment")}
          </Text>
        </div>
      </Card>
    </>
  )
}

export default Payment
